import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Clock } from 'lucide-react';
import { format, addDays, addWeeks, addMonths, setHours, setMinutes } from 'date-fns';

export default function ActionItemButtons({ itemType, itemId, onDismiss, onSnooze }) {
  const [showSnooze, setShowSnooze] = useState(false);
  const [customDate, setCustomDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState('9');
  const [selectedMinute, setSelectedMinute] = useState('0');

  const handleSnooze = (duration) => {
    let snoozeUntil;
    const now = new Date();
    const hour = parseInt(selectedHour);
    const minute = parseInt(selectedMinute);
    
    if (duration === 'later_today') {
      snoozeUntil = setMinutes(setHours(now, hour), minute);
      // If the time is in the past, move to tomorrow
      if (snoozeUntil <= now) {
        snoozeUntil = addDays(snoozeUntil, 1);
      }
    } else if (duration === 'tomorrow') {
      snoozeUntil = setMinutes(setHours(addDays(now, 1), hour), minute);
    } else if (duration === 'next_week') {
      snoozeUntil = setMinutes(setHours(addWeeks(now, 1), hour), minute);
    } else if (duration === 'next_month') {
      snoozeUntil = setMinutes(setHours(addMonths(now, 1), hour), minute);
    } else if (duration === 'custom' && customDate) {
      snoozeUntil = setMinutes(setHours(customDate, hour), minute);
    }
    
    if (snoozeUntil) {
      onSnooze(itemType, itemId, snoozeUntil);
      setShowSnooze(false);
      setCustomDate(null);
    }
  };

  return (
    <div className="flex gap-2">
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onDismiss(itemType, itemId)}
        className="h-6 px-2 text-xs text-slate-400 hover:text-red-400"
      >
        <X className="w-3 h-3 mr-1" />
        Dismiss
      </Button>
      
      <Popover open={showSnooze} onOpenChange={setShowSnooze}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-slate-400 hover:text-amber-400"
          >
            <Clock className="w-3 h-3 mr-1" />
            Snooze
          </Button>
        </PopoverTrigger>
        <PopoverContent className="bg-white border-slate-300 w-80" align="end">
          <div className="space-y-3">
            <Label className="text-slate-900 text-sm font-semibold">Snooze until:</Label>
            
            {/* Time Selection */}
            <div className="flex gap-2 items-center">
              <Label className="text-slate-600 text-xs">Time:</Label>
              <Select value={selectedHour} onValueChange={setSelectedHour}>
                <SelectTrigger className="w-20 h-8 text-xs bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-slate-600">:</span>
              <Select value={selectedMinute} onValueChange={setSelectedMinute}>
                <SelectTrigger className="w-20 h-8 text-xs bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['0', '15', '30', '45'].map((min) => (
                    <SelectItem key={min} value={min}>
                      {min.padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-400"
                onClick={() => handleSnooze('later_today')}
              >
                Later Today ({selectedHour.padStart(2, '0')}:{selectedMinute.padStart(2, '0')})
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-400"
                onClick={() => handleSnooze('tomorrow')}
              >
                Tomorrow ({selectedHour.padStart(2, '0')}:{selectedMinute.padStart(2, '0')})
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-400"
                onClick={() => handleSnooze('next_week')}
              >
                Next Week ({selectedHour.padStart(2, '0')}:{selectedMinute.padStart(2, '0')})
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-400"
                onClick={() => handleSnooze('next_month')}
              >
                Next Month ({selectedHour.padStart(2, '0')}:{selectedMinute.padStart(2, '0')})
              </Button>
              <div className="pt-2 border-t border-slate-300">
                <Label className="text-slate-600 text-xs mb-2 block">Select Custom Date</Label>
                <Calendar
                  mode="single"
                  selected={customDate}
                  onSelect={setCustomDate}
                  className="rounded-md border border-slate-300 bg-slate-50"
                  disabled={(date) => date < new Date()}
                />
                {customDate && (
                  <Button
                    size="sm"
                    className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => handleSnooze('custom')}
                  >
                    Snooze until {format(customDate, 'MMM d, yyyy')} {selectedHour.padStart(2, '0')}:{selectedMinute.padStart(2, '0')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}